/* eslint-disable @typescript-eslint/no-unused-vars */
// ** React Import
import { Fragment, useEffect, useRef, useState } from 'react'

// ** MUI Imports
import List from '@mui/material/List'
import Box, { BoxProps } from '@mui/material/Box'
import { createTheme, responsiveFontSizes, styled, ThemeProvider } from '@mui/material/styles'

// ** Third Party Components
import PerfectScrollbar from 'react-perfect-scrollbar'

// ** Type Import
import { LayoutProps } from 'src/@core/layouts/types'

// ** Theme Config
import themeConfig from 'src/configs/themeConfig'

// ** Component Imports
import DrawerDesktop from './Drawer'
import VerticalNavItems from './VerticalNavItems'
import VerticalNavHeader from './VerticalNavHeader'

// ** Theme Options
import themeOptions from 'src/@core/theme/ThemeOptions'

// ** Util Import
import { hexToRGBA } from 'src/@core/utils/hex-to-rgba'

import { Button, Divider, Drawer, Stack, Typography, useMediaQuery } from '@mui/material'
import { useWebSocket } from '@/src/hooks/useWebSocket'
import { RootState } from '@/src/store'
import { useSelector } from 'react-redux'
import { Icon } from '@iconify/react'
import GygDrawer from '@/src/gygComponents/gygFaqDrawer'
import Link from 'next/link'
import { useAuth } from '@/src/hooks/useAuth'
import { Country, CountryNameMap } from '@/src/types/apps/resto365Countries'
import { sessionCountry } from '@/src/@core/lib/session'
import { IconAUS, IconUSA } from '@/src/gygComponents/CountryFlag/flags'
import NotificationDropdown from '../../shared-components/NotificationDropdown'
import { formatNotificationTimestamp } from '@/src/@core/lib/formatNotificationTimestamp'
import AvatarsLetter from '@/src/gygComponents/AvatarsLetter'
import {
  SidebarNavExperienceToggle,
  useSidebarNavExperienceToggleSlotVisible
} from '@/src/components/featureExperience/SidebarNavExperienceToggle'
import { isIframeContext } from '@/src/utils/iframeUtils'

// LEGACY_MATERIAL_NAV — classic vertical drawer footer hosts experience toggle; remove with Materio sidebar.

interface Props {
  navWidth: number
  navVisible: boolean
  mainNavHidden?: boolean
  collapsedNavWidth: number
  hidden: LayoutProps['hidden']
  navigationBorderWidth: number
  toggleNavVisibility: () => void
  settings: LayoutProps['settings']
  children: LayoutProps['children']
  setNavVisible: (value: boolean) => void
  saveSettings: LayoutProps['saveSettings']
  navMenuContent: LayoutProps['verticalLayoutProps']['navMenu']['content']
  navMenuBranding: LayoutProps['verticalLayoutProps']['navMenu']['branding']
  menuLockedIcon: LayoutProps['verticalLayoutProps']['navMenu']['lockedIcon']
  verticalNavItems: LayoutProps['verticalLayoutProps']['navMenu']['navItems']
  navMenuProps: LayoutProps['verticalLayoutProps']['navMenu']['componentProps']
  menuUnlockedIcon: LayoutProps['verticalLayoutProps']['navMenu']['unlockedIcon']
  afterNavMenuContent: LayoutProps['verticalLayoutProps']['navMenu']['afterContent']
  beforeNavMenuContent: LayoutProps['verticalLayoutProps']['navMenu']['beforeContent']
}

const StyledBoxForShadow = styled(Box)<BoxProps>(({ theme }) => ({
  top: 60,
  left: -8,
  zIndex: 2,
  opacity: 0,
  position: 'absolute',
  pointerEvents: 'none',
  width: 'calc(100% + 15px)',
  height: theme.mixins.toolbar.minHeight,
  transition: 'opacity .15s ease-in-out',
  background: `linear-gradient(${theme.palette.background.paper} ${
    theme.direction === 'rtl' ? '95%' : '5%'
  },${hexToRGBA(theme.palette.background.paper, 0.85)} 30%,${hexToRGBA(
    theme.palette.background.paper,
    0.5
  )} 65%,${hexToRGBA(theme.palette.background.paper, 0.3)} 75%,transparent)`
}))

type NotificationCategory = 'menu' | 'restaurant'

function getCategoryLabel(category: NotificationCategory) {
  switch (category) {
    case 'menu':
      return 'Menu'
    case 'restaurant':
      return 'Restaurant'
    default:
      return ''
  }
}

const Navigation = (props: Props) => {
  const [openDrawer, setOpenDrawer] = useState(false)
  const { isConnected } = useWebSocket()
  const { logout, user, country } = useAuth()
  const {
    notifications: { value: notifications }
  } = useSelector((state: RootState) => state.resto365Notifications)

  // ** Props
  const {
    hidden,
    settings,
    mainNavHidden,
    afterNavMenuContent,
    beforeNavMenuContent,
    navigationBorderWidth,
    navMenuContent: userNavMenuContent
  } = props

  // ** States
  const [navHover, setNavHover] = useState<boolean>(false)
  const [groupActive, setGroupActive] = useState<string[]>([])
  const [currentActiveGroup, setCurrentActiveGroup] = useState<string[]>([])
  const { navCollapsed } = settings

  // ** Ref
  const shadowRef = useRef(null)

  // ** Var
  const { afterVerticalNavMenuContentPosition, beforeVerticalNavMenuContentPosition } = themeConfig

  const isLandscape = useMediaQuery('(max-width:1200px) and (orientation: landscape)')
  const isPortrait = useMediaQuery('(max-width:1024px) and (orientation: portrait)')
  const showSidebarNavExperienceSlot = useSidebarNavExperienceToggleSlotVisible(false)
  const showSidebarNavExperienceSlotCollapsed = useSidebarNavExperienceToggleSlotVisible(true)

  const navMenuContentProps = {
    ...props,
    navHover,
    groupActive,
    setGroupActive,
    currentActiveGroup,
    setCurrentActiveGroup
  }

  // ** Create new theme for the navigation menu when mode is `semi-dark`
  let darkTheme = createTheme(themeOptions(settings, 'dark'))

  // ** Set responsive font sizes to true
  if (themeConfig.responsiveFontSizes) {
    darkTheme = responsiveFontSizes(darkTheme)
  }

  // ** Fixes Navigation InfiniteScroll
  const handleInfiniteScroll = (ref: HTMLElement) => {
    if (ref) {
      // @ts-ignore
      ref._getBoundingClientRect = ref.getBoundingClientRect

      ref.getBoundingClientRect = () => {
        // @ts-ignore
        const original = ref._getBoundingClientRect()

        return { ...original, height: Math.floor(original.height) }
      }
    }
  }

  // ** Scroll Menu
  const scrollMenu = (container: any) => {
    if (beforeVerticalNavMenuContentPosition === 'static' || !beforeNavMenuContent) {
      container = hidden ? container.target : container
      if (shadowRef && container.scrollTop > 0) {
        // @ts-ignore
        if (!shadowRef.current.classList.contains('scrolled')) {
          // @ts-ignore
          shadowRef.current.classList.add('scrolled')
        }
      } else {
        // @ts-ignore
        shadowRef.current.classList.remove('scrolled')
      }
    }
  }

  const ScrollWrapper = hidden ? Box : PerfectScrollbar

  const menuCollapsedStyles =
    navCollapsed && !navHover
      ? {
          opacity: 0,
          transition:
            'padding .25s ease-in-out, width .25s ease-in-out, box-shadow .25s ease-in-out, border-color .25s ease-in-out'
        }
      : {
          opacity: 1,
          transition:
            'padding .25s ease-in-out, width .25s ease-in-out, box-shadow .25s ease-in-out, border-color .25s ease-in-out'
        }
  const toggleFAQDrawer = (newOpen: boolean) => {
    setOpenDrawer(newOpen)
  }

  return (
    <ThemeProvider theme={darkTheme}>
      <DrawerDesktop
        {...props}
        navHover={navHover}
        setNavHover={setNavHover}
        navigationBorderWidth={navigationBorderWidth}
      >
        <VerticalNavHeader {...props} navHover={navHover} mainNavHidden={mainNavHidden} />
        {beforeNavMenuContent && beforeVerticalNavMenuContentPosition === 'fixed'
          ? beforeNavMenuContent(navMenuContentProps)
          : null}
        {(beforeVerticalNavMenuContentPosition === 'static' || !beforeNavMenuContent) && (
          <StyledBoxForShadow ref={shadowRef} />
        )}
        <Box sx={{ position: 'relative', height: '100vh', overflow: 'hidden' }} className='box-data'>
          {/* @ts-ignore */}
          <ScrollWrapper
            {...(hidden
              ? {
                  onScroll: (container: any) => scrollMenu(container)
                }
              : {
                  options: { wheelPropagation: false },
                  onScrollY: (container: any) => scrollMenu(container),
                  containerRef: (ref: any) => handleInfiniteScroll(ref)
                })}
          >
            {beforeNavMenuContent && beforeVerticalNavMenuContentPosition === 'static'
              ? beforeNavMenuContent(navMenuContentProps)
              : null}
            {userNavMenuContent ? (
              userNavMenuContent(navMenuContentProps)
            ) : (
              <Fragment>
                <List
                  className='nav-items'
                  sx={{
                    pt: 0,
                    pb: '250px', // Significantly increased padding bottom to prevent overlap with bottom stack
                    '& > :first-of-type': { mt: '0' }
                  }}
                >
                  <VerticalNavItems
                    navHover={navHover}
                    groupActive={groupActive}
                    setGroupActive={setGroupActive}
                    currentActiveGroup={currentActiveGroup}
                    setCurrentActiveGroup={setCurrentActiveGroup}
                    {...props}
                  />
                </List>
                <Divider />
              </Fragment>
            )}
            {afterNavMenuContent && afterVerticalNavMenuContentPosition === 'static'
              ? afterNavMenuContent(navMenuContentProps)
              : null}
          </ScrollWrapper>
          {!navCollapsed && (
            <Stack
              direction={'column'}
              sx={{
                position: 'fixed',
                bottom: '0px',
                paddingTop: '10px',
                backgroundColor: '#000',
                zIndex: 999,
                borderTop: '1px solid #646464',
                transition:
                  'width .25s cubic-bezier(0.4,0,0.2,1), box-shadow .25s cubic-bezier(0.4,0,0.2,1), transform .25s cubic-bezier(0.4,0,0.2,1), background-color .25s cubic-bezier(0.4,0,0.2,1)',
                transform: 'translateX(0)'
              }}
              width={themeConfig.navigationSize}
            >
              <Stack
                direction={'column'}
                gap={'12px'}
                marginBottom={0}
                padding={2}
                alignItems={'flex-start'}
                className='nav-items app-bar-content-custom'
                sx={{
                  ...menuCollapsedStyles,
                  ...(navCollapsed && !navHover ? {} : { ml: 2.5 })
                }}
              >
                {isLandscape || isPortrait ? null : (
                  <Box gap={2} sx={{ userSelect: 'none' }}>
                    <Stack direction={'row'} gap={3} alignItems={'center'}>
                      <NotificationDropdown
                        settings={{
                          skin: 'default',
                          mode: 'light',
                          appBarBlur: false,
                          direction: 'ltr',
                          navCollapsed: false,
                          themeColor: 'primary',
                          contentWidth: 'full',
                          verticalNavToggleType: 'accordion'
                        }}
                        sidebar={true}
                        isSocketConnected={isConnected}
                        notifications={notifications.map(notification => {
                          const { message, category, createdAt, read, clientKey } = notification

                          return {
                            meta: getCategoryLabel(category),
                            title: message,
                            subtitle: formatNotificationTimestamp(createdAt),
                            read,
                            clientKey: clientKey ?? ''
                          }
                        })}
                      />
                    </Stack>
                  </Box>
                )}
                <Box padding='8px' gap={2} sx={{ userSelect: 'none', width: '100%', justifyContent: 'flex-start' }}>
                  <Stack direction={'row'} gap={3} alignItems={'center'}>
                    {country === Country.AUS ? IconAUS() : IconUSA()}
                    <Typography variant='body1' sx={{ color: '#fff' }}>
                      {CountryNameMap[sessionCountry]}
                    </Typography>
                  </Stack>
                </Box>

                {isLandscape || isPortrait ? null : (
                  <Button
                    onClick={() => toggleFAQDrawer(true)}
                    sx={{ marginLeft: -1, color: '#fff', width: '100%', justifyContent: 'flex-start' }}
                  >
                    <Stack direction={'row'} gap={3} alignItems={'center'}>
                      <Icon fontSize='22px' color='#fff' icon='akar-icons:info' />
                      <Typography variant='body1' sx={{ color: '#fff' }}>
                        Support
                      </Typography>
                    </Stack>
                  </Button>
                )}
                <Drawer anchor='right' open={openDrawer} sx={{ width: '30%', position: 'relative' }}>
                  <Box sx={{ position: 'absolute', top: '25px', right: '20px' }}>
                    <Icon onClick={() => toggleFAQDrawer(false)} icon='material-symbols:close' />
                  </Box>
                  {<GygDrawer />}
                </Drawer>
              </Stack>
              {user && !isIframeContext() && showSidebarNavExperienceSlot ? (
                <Box
                  sx={{
                    px: 2,
                    py: 1.5,
                    width: '100%',
                    borderTop: '1px solid #646464'
                  }}
                >
                  <SidebarNavExperienceToggle />
                </Box>
              ) : null}
              <Box
                padding='10px 15px'
                gap={2}
                sx={{
                  userSelect: 'none',
                  cursor: 'pointer',
                  width: '100%',
                  justifyContent: 'flex-start',
                  borderTop: '1px solid #646464'
                }}
                onClick={() => logout()}
              >
                <Stack
                  direction={'row'}
                  gap={2}
                  alignItems={'center'}
                  sx={{
                    ...menuCollapsedStyles,
                    ...(navCollapsed && !navHover ? {} : { ml: 2.5 }),
                    width: '100%',
                    justifyContent: 'flex-start'
                  }}
                  className='nav-items app-bar-content-custom'
                >
                  <AvatarsLetter name={user!.fullName} role={user!.role.name} />
                  <Typography variant='caption'>
                    <Button
                      variant='text'
                      style={{
                        userSelect: 'none',
                        color: '#fff',
                        fontFamily: 'Montserrat',
                        fontSize: '12px',
                        fontWeight: '400',
                        textDecoration: 'underline',
                        width: '100%',
                        justifyContent: 'flex-start'
                      }}
                    >
                      Log&nbsp;out
                    </Button>
                  </Typography>
                </Stack>
              </Box>
            </Stack>
          )}
          {navCollapsed && (
            <Stack
              direction={'column'}
              sx={{
                position: 'fixed',
                bottom: '0px',
                margin: 0,
                padding: 0,
                backgroundColor: '#000',
                zIndex: 999,
                borderTop: '1px solid #646464',
                transition: 'width .25s ease-in-out,box-shadow .25s ease-in-out, border-top .25s ease-in-out',
                transform: 'translateX(0)'
              }}
              width={'80px'}
            >
              <Stack
                direction={'column'}
                marginBottom={0}
                padding={2}
                alignItems={'center'}
                className='nav-items app-bar-content-custom'
              >
                {isLandscape || isPortrait ? null : (
                  <NotificationDropdown
                    settings={{
                      skin: 'default',
                      mode: 'light',
                      appBarBlur: false,
                      direction: 'ltr',
                      navCollapsed: navCollapsed,
                      themeColor: 'primary',
                      contentWidth: 'full',
                      verticalNavToggleType: 'accordion'
                    }}
                    sidebar={true}
                    isSocketConnected={isConnected}
                    notifications={notifications.map(notification => {
                      const { message, category, createdAt, read, clientKey } = notification

                      return {
                        meta: getCategoryLabel(category),
                        title: message,
                        subtitle: formatNotificationTimestamp(createdAt),
                        read,
                        clientKey: clientKey ?? ''
                      }
                    })}
                  />
                )}
                <Typography variant='h2' padding='10px' sx={{ userSelect: 'none' }}>
                  {country === Country.AUS ? IconAUS() : IconUSA()}
                </Typography>
                <Button title='FAQ' onClick={() => toggleFAQDrawer(true)} sx={{ marginLeft: -1, color: '#fff' }}>
                  <Stack direction={'row'} gap={2} marginBottom={0}>
                    <Icon fontSize={24} color='#fff' icon='akar-icons:info' />
                  </Stack>
                </Button>
                <Drawer anchor='right' open={openDrawer} sx={{ width: '30%', position: 'relative' }}>
                  <Box sx={{ position: 'absolute', top: '25px', right: '20px' }}>
                    <Icon onClick={() => toggleFAQDrawer(false)} icon='material-symbols:close' />
                  </Box>
                  {<GygDrawer />}
                </Drawer>
              </Stack>
              {user && !isIframeContext() && showSidebarNavExperienceSlotCollapsed ? (
                <Box
                  sx={{
                    py: 1.5,
                    width: '100%',
                    borderTop: '1px solid #646464',
                    display: 'flex',
                    justifyContent: 'center'
                  }}
                >
                  <SidebarNavExperienceToggle collapsed />
                </Box>
              ) : null}
              <Stack
                direction={'column'}
                padding={4}
                alignItems={'center'}
                className='nav-items app-bar-content-custom'
                sx={{
                  borderTop: '1px solid #646464'
                }}
              >
                <AvatarsLetter name={user!.fullName} role={user!.role.name} />
                <Typography variant='caption'>
                  <Button variant='text' onClick={() => logout()} style={{ color: '#fff' }}>
                    Log out
                  </Button>
                </Typography>
              </Stack>
            </Stack>
          )}
        </Box>
        {afterNavMenuContent && afterVerticalNavMenuContentPosition === 'fixed'
          ? afterNavMenuContent(navMenuContentProps)
          : null}
      </DrawerDesktop>
    </ThemeProvider>
  )
}

export default Navigation
